import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import styles from './OfferDetailPage.module.css';

interface StatusDropdownProps {
  currentStatus: string;
  statusColor: { bg: string; text: string };
  statusLabel: string;
  onChangeStatus: (status: string) => void;
  disabled?: boolean;
}

export function StatusDropdown({ currentStatus, statusColor, statusLabel, onChangeStatus, disabled }: StatusDropdownProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const statusOptions = [
    { value: 'DRAFT', label: 'Entwurf', color: { bg: '#f3f4f6', text: '#374151' } },
    { value: 'SENT', label: 'Versendet', color: { bg: '#dbeafe', text: '#1e40af' } },
    { value: 'ACCEPTED', label: 'Akzeptiert', color: { bg: '#d1fae5', text: '#065f46' } },
    { value: 'REJECTED', label: 'Abgelehnt', color: { bg: '#fee2e2', text: '#991b1b' } },
    { value: 'INVOICED', label: 'Berechnet', color: { bg: '#ddd6fe', text: '#5b21b6' } },
  ];

  const availableTransitions: Record<string, string[]> = {
    DRAFT: ['SENT'],
    SENT: ['ACCEPTED', 'REJECTED'],
    ACCEPTED: ['SENT', 'REJECTED'],
    REJECTED: ['SENT', 'ACCEPTED'],
    INVOICED: [],
  };

  const allowedStatuses = availableTransitions[currentStatus] || [];

  return (
    <div ref={dropdownRef} className={styles.statusDropdownContainer}>
      <button
        onClick={() => !disabled && allowedStatuses.length > 0 && setIsOpen(!isOpen)}
        disabled={disabled || allowedStatuses.length === 0}
        className={styles.statusDropdownBtn}
        style={{
          background: statusColor.bg,
          color: statusColor.text,
          cursor: disabled || allowedStatuses.length === 0 ? 'default' : 'pointer',
        }}
      >
        {statusLabel}
        {allowedStatuses.length > 0 && (
          <FaChevronDown
            size={10}
            className={styles.statusDropdownChevron}
            style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
          />
        )}
      </button>
      {isOpen && (
        <div className={styles.statusDropdownMenu}>
          {statusOptions
            .filter((opt) => allowedStatuses.includes(opt.value))
            .map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChangeStatus(option.value);
                  setIsOpen(false);
                }}
                className={styles.statusDropdownItem}
              >
                <span
                  className={styles.statusDot}
                  style={{
                    background: option.color.bg,
                    border: `2px solid ${option.color.text}`,
                  }}
                />
                {option.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
